import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { adminApiClient } from "../../services/apiClient";
import { toApiError } from "../../types/api";

interface Voice {
  id: string;
  name: string;
  language: string;
  provider: string;
  tier: string;
  status: string;
  gender: string;
  enabled: boolean;
}

interface Language {
  code: string;
  name: string;
  count: number;
}

interface VoiceStats {
  total: number;
  byProvider: Record<string, number>;
  byLanguage: Record<string, number>;
  byTier: Record<string, number>;
}

interface AdminVoicesState {
  voices: Voice[];
  languages: Language[];
  stats: VoiceStats | null;
  loading: boolean;
  error: string | null;
  bulkOperation: "tier" | "status" | null;
  showAddVoiceModal: boolean;
}

const initialState: AdminVoicesState = {
  voices: [],
  languages: [],
  stats: null,
  loading: false,
  error: null,
  bulkOperation: null,
  showAddVoiceModal: false,
};

// Async thunks
export const fetchVoices = createAsyncThunk<
  { voices: Voice[]; stats: VoiceStats },
  { page?: number; limit?: number; language?: string; tier?: string },
  { rejectValue: string }
>("adminVoices/fetchVoices", async (params = {}, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.language) queryParams.append("language", params.language);
    if (params.tier) queryParams.append("tier", params.tier);

    const response = await adminApiClient.get(
      `/admin/voices?${queryParams.toString()}`,
    );
    return {
      voices: response.data.voices || [],
      stats: response.data.stats || {},
    };
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchLanguages = createAsyncThunk<
  Language[],
  void,
  { rejectValue: string }
>("adminVoices/fetchLanguages", async (_, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.get("/admin/texts/languages");
    return response.data.languages;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchVoiceStats = createAsyncThunk<
  VoiceStats,
  void,
  { rejectValue: string }
>("adminVoices/fetchStats", async (_, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.get("/admin/voices/stats");
    return response.data.stats;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const updateVoiceStatus = createAsyncThunk<
  { id: string; enabled: boolean },
  { id: string; enabled: boolean },
  { rejectValue: string }
>("adminVoices/updateStatus", async ({ id, enabled }, { rejectWithValue }) => {
  try {
    await adminApiClient.patch(`/admin/voices/${id}`, { enabled });
    return { id, enabled };
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const initializeVoices = createAsyncThunk<
  { message: string; status: string },
  void,
  { rejectValue: string }
>("adminVoices/initialize", async (_, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.post("/admin/voices/initialize");
    return response.data;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const reinitializeVoices = createAsyncThunk<
  { message: string; stats: VoiceStats },
  void,
  { rejectValue: string }
>("adminVoices/reinitialize", async (_, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.post("/admin/voices/reinitialize");
    return response.data;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const setDefaultVoiceTiers = createAsyncThunk<
  { message: string; status: string },
  void,
  { rejectValue: string }
>("adminVoices/setDefaultTiers", async (_, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.post(
      "/admin/voices/set-default-tiers",
    );
    return response.data;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

const adminVoicesSlice = createSlice({
  name: "adminVoices",
  initialState,
  reducers: {
    setBulkOperation: (state, action) => {
      state.bulkOperation = action.payload;
    },
    setShowAddVoiceModal: (state, action) => {
      state.showAddVoiceModal = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch voices
      .addCase(fetchVoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVoices.fulfilled, (state, action) => {
        state.loading = false;
        state.voices = action.payload.voices;
        state.stats = action.payload.stats;
      })
      .addCase(fetchVoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch voices";
      })
      // Fetch languages
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.languages = action.payload;
      })
      .addCase(fetchLanguages.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch languages";
      })
      // Fetch stats
      .addCase(fetchVoiceStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(fetchVoiceStats.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch stats";
      })
      // Update voice status
      .addCase(updateVoiceStatus.fulfilled, (state, action) => {
        const { id, enabled } = action.payload;
        const voice = state.voices.find((v) => v.id === id);
        if (voice) {
          voice.enabled = enabled;
        }
      })
      .addCase(updateVoiceStatus.rejected, (state, action) => {
        state.error = action.payload || "Failed to update voice status";
      })
      // Initialize voices
      .addCase(initializeVoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeVoices.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initializeVoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to initialize voices";
      })
      // Reinitialize voices
      .addCase(reinitializeVoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reinitializeVoices.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.stats;
      })
      .addCase(reinitializeVoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to reinitialize voices";
      })
      // Set default voice tiers
      .addCase(setDefaultVoiceTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setDefaultVoiceTiers.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(setDefaultVoiceTiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to set default voice tiers";
      });
  },
});

export const { setBulkOperation, setShowAddVoiceModal, clearError } =
  adminVoicesSlice.actions;

// Export with aliases for compatibility
export { fetchVoices as fetchAdminVoices };
export { fetchLanguages as fetchVoiceLanguages };

export default adminVoicesSlice.reducer;
